
import { SizeTable, SizeTableEntry } from "@/models/base";
import { SizeTableService } from "@/services/size-table-service";
import Vue from "vue";

export default Vue.extend({
	data() {
		return {
			sizeTables: [] as SizeTable[],
			toEdit: null as SizeTable | null,
			removedEntryIds: [] as number[],
			keySeq: 0,
			saving: false
		};
	},
	computed: {
		saveEnabled():boolean {
			if (!this.toEdit) return false;
			if (this.toEdit.Name.match(/^\s*$/)) return false;
			return true;
		}
	},
	methods: {
		async load() {
			this.sizeTables = await SizeTableService.getAll();
		},
		addTable() {
			this.removedEntryIds = [];
			this.toEdit = new SizeTable();
		},
		editTable(table:SizeTable) {
			this.removedEntryIds = [];
			// Edit a copy so cancel doesn't mutate the list.
			let copy = new SizeTable(table.getJSON());
			copy.Entries = table.getSortedEntries().map(e => {
				let entry = new SizeTableEntry(e.getJSON());
				entry.clientKey = "e" + entry.ID;
				return entry;
			});
			this.toEdit = copy;
		},
		addEntry() {
			if (!this.toEdit) return;
			let entry = new SizeTableEntry();
			entry.clientKey = "n" + (this.keySeq++);
			this.toEdit.Entries.push(entry);
		},
		removeEntry(entry:SizeTableEntry) {
			if (!this.toEdit) return;
			let index = this.toEdit.Entries.indexOf(entry);
			if (index === -1) return;
			if (entry.ID) {
				this.removedEntryIds.push(entry.ID);
			}
			this.toEdit.Entries.splice(index, 1);
		},
		onSorted() {
			// w-sortable reorders toEdit.Entries in place; reflect the new order in DisplayOrder.
			if (!this.toEdit) return;
			this.toEdit.Entries.forEach((entry, i) => {
				entry.DisplayOrder = i;
			});
		},
		async saveTable() {
			if (!this.toEdit || !this.saveEnabled) return;
			this.saving = true;
			try {
				let table = this.toEdit.ID
					? await SizeTableService.update(this.toEdit)
					: await SizeTableService.create(this.toEdit);

				// Persist entries via the dedicated endpoint so we don't depend on nested saving.
				for (let id of this.removedEntryIds) {
					await SizeTableService.deleteEntry(id);
				}
				for (let i = 0; i < this.toEdit.Entries.length; i++) {
					let entry = this.toEdit.Entries[i];
					entry.SizeTableID = table.ID;
					entry.DisplayOrder = i;
					if (entry.ID) {
						await SizeTableService.updateEntry(entry);
					} else {
						await SizeTableService.createEntry(entry);
					}
				}

				await this.load();
				this.closeModal();
			} catch (err) {
				this.$wf.notify("ERROR", "danger", 3000, "exclamation-triangle");
			} finally {
				this.saving = false;
			}
		},
		async deleteTable(table:SizeTable) {
			let confirm = await this.$wf.confirm(
				this.$ct("confirm-delete-size-table", {name: table.Name}),
				this.$t("common.yes"), this.$t("common.no"), this.$ct("common.warning"), "info"
			);
			if (!confirm) return;
			try {
				await SizeTableService.delete(table.ID);
				await this.load();
			} catch (err) {
				this.$wf.notify("ERROR", "danger", 3000, "exclamation-triangle");
			}
		},
		closeModal() {
			this.toEdit = null;
			this.removedEntryIds = [];
		}
	},
	mounted() {
		this.load();
	},
	i18nEx: {
		componentPrefix: "components.settings-default"
	}
});
